<template>
  <layout-loading v-if="loading" />
  <div v-else-if="!loaded" class="text-center">
    <p>{{ $t("views.profile.community.loading.error") }}</p>
    <icon-button variant="primary" icon="arrow-clockwise" :onclick="load">{{
      $t("views.profile.community.loading.retry")
    }}</icon-button>
  </div>
  <div v-else class="profile-community">
    <h2 class="mb-3 font-weight-bold text-primary">{{ communityUser.community.name }}</h2>

    <b-card v-if="showContributionBox" class="profile-community__card">
      <form-section
        toggleable
        inititally-visible
        tag="h3"
        :section-title="$t('views.profile.community.contribution.title')"
      >
        <profile-community-subscriptions
          :community="communityUser.community"
          :pricings-per-loanable-type="communityUser.new_subscription_pricings"
          :current-granted-subscription="communityUser.current_granted_subscription"
          :current-paid-subscription="communityUser.current_paid_subscription"
          @paid="load"
        />
      </form-section>
    </b-card>
    <hr v-if="showContributionBox" class="mt-0" />

    <b-card v-if="!hadInitiallyAcceptedObnlMembership" class="mb-3">
      <accept-obnl-membership-section v-model="acceptsObnlMembership" />
    </b-card>

    <b-card
      :class="`profile-community__card ${
        acceptsObnlMembership ? 'profile-community__card--accepted' : ''
      }`"
    >
      <form-section
        :section-title="$t('views.profile.community.proofs.title')"
        :toggleable="!!showContributionBox"
        :inititally-visible="!showContributionBox || communityUser.status !== 'approved'"
        tag="h3"
      >
        <community-proof-form
          :community-user="communityUser"
          :disabled="!acceptsObnlMembership"
          :user="user"
          @submitted="onSubmitted"
        />
      </form-section>
    </b-card>
  </div>
</template>

<script>
import AcceptObnlMembershipSection from "@/components/Community/AcceptObnlMembershipSection.vue";
import CommunityProofForm from "@/components/Community/ProofForm.vue";
import FormSection from "@/components/Loanable/FormSection.vue";
import IconButton from "@/components/shared/IconButton.vue";
import LayoutLoading from "@/components/Layout/Loading.vue";

import { get } from "@/requests/server";

import ProfileCommunitySubscriptions from "@/views/profile/CommunitySubscriptions.vue";

export default {
  name: "ProfileCommunities",
  components: {
    AcceptObnlMembershipSection,
    IconButton,
    LayoutLoading,
    CommunityProofForm,
    ProfileCommunitySubscriptions,
    FormSection,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      acceptsObnlMembership: true,
      hadInitiallyAcceptedObnlMembership: true,
      loaded: false,
      loading: true,
      communityUser: [],
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    showContributionBox() {
      return (
        this.communityUser.status === "approved" &&
        (this.communityUser.new_subscription_pricings ||
          this.communityUser.current_granted_subscription ||
          this.communityUser.current_paid_subscription)
      );
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;

      const { data } = await get(
        `/users/${this.$store.state.user.id}/communities/${this.id}?relations=community,custom_proof,current_granted_subscription.granted_by_user,current_paid_subscription`,
        {
          notifications: { action: this.$t("views.profile.community.loading.action") },
          cleanupCallback: () => (this.loading = false),
        }
      );
      this.loaded = true;
      this.communityUser = data;
      this.acceptsObnlMembership = !!this.user.meta.accepted_obnl_membership;
      this.hadInitiallyAcceptedObnlMembership = !!this.user.meta.accepted_obnl_membership;
    },
    onSubmitted() {
      // Reload user with updated proofs
      this.$store.dispatch("loadUser");

      if (this.communityUser.status !== "approved") {
        this.$store.commit("addNotification", {
          content: this.$t("views.profile.community.proofs.submitted.message"),
          title: this.$t("views.profile.community.proofs.submitted.title"),
          variant: "success",
        });
        this.$router.push("/profile/communities");
      } else {
        this.$store.commit("addNotification", {
          title: this.$t("views.profile.community.profile.updated.title"),
          variant: this.$t("views.profile.community.profile.updated.variant"),
        });
      }
    },
  },
};
</script>

<style lang="scss">
.profile-community {
  &__card {
    box-shadow: $small-shadow;
    opacity: 0.5;
    transition: opacity 0.2s ease-in-out;

    &--accepted {
      opacity: 1;
    }
  }

  .form__section {
    padding-bottom: 0;
    h3 {
      margin-bottom: 1rem;
    }
  }
}
</style>
